import { create } from 'zustand'
import { battleApi } from '../services/battleApi'
import useAppStore from '../../../store/useAppStore'
import audioService from '../../../services/audioService'
import gameSocket from '../../crash/services/crashSocket'

let battleListRequestId = 0

const socketAction = (event, payload) => new Promise((resolve, reject) => {
  if (!gameSocket.isConnected()) return reject(new Error('disconnected'))
  const clientId = `${event === 'casebattle:create' ? 'c' : 'j'}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  const okEvent = `${event}:ok`
  const cleanup = () => {
    clearTimeout(timer)
    gameSocket.off(okEvent, onOk)
    gameSocket.off('user:alert', onAlert)
    gameSocket.offDisconnect(onDisconnect)
  }
  const onOk = (data) => {
    if (data.client_id !== clientId) return
    cleanup()
    resolve(data)
  }
  const onAlert = (data) => {
    if (data.game !== 'casebattle' || data.client_id !== clientId) return
    cleanup()
    reject(new Error(data.code))
  }
  const onDisconnect = () => {
    cleanup()
    reject(new Error('disconnected'))
  }
  const timer = setTimeout(() => {
    cleanup()
    gameSocket.emit('casebattle:state')
    reject(new Error('action_unconfirmed'))
  }, 5000)
  gameSocket.on(okEvent, onOk)
  gameSocket.on('user:alert', onAlert)
  gameSocket.onDisconnect(onDisconnect)
  if (!gameSocket.emit(event, { ...payload, client_id: clientId })) onDisconnect()
})

const normalizeBattleCases = (casesData) => {
  if (Array.isArray(casesData)) {
    return casesData
  }

  if (casesData && typeof casesData === 'object') {
    return Object.values(casesData)
  }

  return []
}

const normalizeCategoryOrder = (categoryOrder) => {
  if (!Array.isArray(categoryOrder)) {
    return []
  }

  // Новый формат: массив объектов {slug, name}
  return categoryOrder.filter(
    (category) =>
      category &&
      typeof category === 'object' &&
      typeof category.slug === 'string' &&
      category.slug.trim() !== ''
  )
}

const normalizeGames = (games) => {
  if (!Array.isArray(games)) {
    return []
  }

  return games.filter(
    (game) =>
      game &&
      typeof game === 'object' &&
      typeof game.slug === 'string' &&
      game.slug.trim() !== ''
  )
}

const useBattleStore = create((set, get) => ({
  // State
  battles: [], // Массив активных баттлов (waiting и finished)
  battlesById: {},
  engine: null,
  hasSnapshot: false,
  isConnected: false,
  isModeDisabled: false,
  limits: {},
  availableCases: [], // Массив доступных кейсов для создания баттла
  categoryOrder: [],
  games: [],
  currentBattle: null,
  isLoading: false,
  isCreating: false,
  error: null,

  // Actions
  
  /**
   * Загрузить список всех баттлов и доступных кейсов
   * API возвращает battles как объект, конвертируем в массив
   * Также получаем список кейсов для создания баттла
   */
  loadBattles: async () => {
    const requestId = ++battleListRequestId
    try {
      set({ isLoading: true, error: null })
      const data = await battleApi.getBattles()
      if (requestId !== battleListRequestId) return
      const normalizedCases = normalizeBattleCases(data.cases)
      const normalizedCategoryOrder = normalizeCategoryOrder(data.categoryOrder)
      const normalizedGames = normalizeGames(data.games)
      
      // data содержит { battles: [], cases: [] }
      set((state) => ({
        ...(state.engine === 'worker' ? { battles: data.battles || [], hasSnapshot: true } : {}),
        availableCases: normalizedCases,
        categoryOrder: normalizedCategoryOrder,
        games: normalizedGames,
        isLoading: false
      }))
    } catch (error) {
      if (requestId !== battleListRequestId) return
      console.error('Load battles error:', error)
      set((state) => ({
        error: error.message || 'Ошибка загрузки баттлов',
        isLoading: false,
        ...(state.engine === 'worker' ? { hasSnapshot: true } : {})
      }))
    }
  },

  /**
   * Загрузить информацию о конкретном баттле
   */
  loadBattle: async (battleId) => {
    try {
      set({ isLoading: true, error: null })
      const battle = await battleApi.getBattle(battleId)
      set({ currentBattle: battle, isLoading: false })
    } catch (error) {
      console.error('Load battle error:', error)
      set({ 
        error: error.message || 'Ошибка загрузки баттла',
        isLoading: false 
      })
    }
  },

  /**
   * Создать новый баттл
   * @param {Object} params - Параметры создания баттла
   * @param {string} params.caseId - ID кейса
   * @param {number} params.countPlayers - Количество игроков (2-4)
   * @param {number} params.countCase - Количество кейсов (1-5)
   * @param {string} params.type - Тип определения победителя ("more" | "less" | "random")
   */
  createBattle: async ({ caseId, countPlayers, countCase, type = 'more' }) => {
    const actionEngine = get().engine
    try {
      set({ isCreating: true, error: null })
      if (actionEngine === 'node') {
        if (get().isModeDisabled) throw new Error('mode_disabled')
        const { id } = await socketAction('casebattle:create', {
          case_id: caseId, count_players: countPlayers, count_case: countCase, type
        })
        set({ isCreating: false })
        return id
      }

      // Вызываем API для создания баттла
      const { battle, user } = await battleApi.createBattle({ 
        caseId, 
        countPlayers, 
        countCase, 
        type 
      })
      
      // Обновляем баланс пользователя в useAppStore
      const appStore = useAppStore.getState()
      if (user && user.balance !== undefined) {
        appStore.setUser({ ...appStore.user, balance: user.balance })
      }
      
      // Добавляем созданный баттл в список battles
      // Преобразуем данные в формат, совместимый со списком
      const newBattle = {
        id: battle.id,
        case: battle.case,
        status: battle.status,
        count_players: battle.count_players,
        count_case: battle.count_case,
        type: battle.type,
        battle_close_in_seconds: battle.battle_close_in_seconds,
        need_balance: battle.need_balance,
        you: 1, // Это наш баттл
        players: [{
          user_id: battle.user_id,
          user_name: battle.user_name,
          user_ava: battle.user_ava,
          user_team: battle.user_team, // банда для бейджа рядом с ником (может быть null)
          slot: battle.slot,
          you: 1
        }]
      }
      
      // Добавляем баттл в начало списка
      const { battles } = get()
      set({ 
        battles: [newBattle, ...battles],
        isCreating: false 
      })
      
      return battle.id
    } catch (error) {
      console.error('Create battle error:', error)
      set({ 
        error: error.message || 'Ошибка создания баттла',
        isCreating: false 
      })
      if (error.code === 'engine_switched' || error.message === 'engine_switched') {
        set({ engine: actionEngine === 'node' ? 'worker' : 'node', hasSnapshot: false, battles: [], battlesById: {} })
        if (actionEngine !== 'node') gameSocket.requestState('casebattle')
      }
      throw error
    }
  },

  /**
   * Присоединиться к баттлу
   * @param {string} battleId - ID баттла
   * @returns {Object} - { battleId, hasResults, results } где hasResults указывает, был ли это последний игрок
   */
  joinBattle: async (battleId) => {
    const target = get().battlesById[battleId] || get().battles.find((item) => item.id === battleId)
    const actionEngine = target?.engine || get().engine
    try {
      set({ isLoading: true, error: null })
      if (actionEngine === 'node') {
        if (get().isModeDisabled) throw new Error('mode_disabled')
        await socketAction('casebattle:join', { id: battleId })
        set({ isLoading: false })
        return { battleId, hasResults: false }
      }

      // Вызываем API для присоединения к баттлу
      const { battle, results, user } = await battleApi.joinBattle(battleId)
      
      // Обновляем баланс пользователя в useAppStore
      const appStore = useAppStore.getState()
      if (user && user.balance !== undefined) {
        appStore.setUser({ ...appStore.user, balance: user.balance })
      }
      
      // Обновляем баттл в списке battles
      const { battles } = get()
      const updatedBattles = battles.map(b => {
        if (b.id === battleId) {
          // Обновляем статус и добавляем нового игрока
          return {
            ...b,
            status: battle.status,
            battle_close_in_seconds: battle.battle_close_in_seconds,
            result_casebattle: results, // Сохраняем результаты если есть
            // Добавляем текущего пользователя в список игроков
            players: [
              ...b.players,
              {
                user_id: battle.user_id,
                user_name: battle.user_name,
                user_ava: battle.user_ava,
                user_team: battle.user_team, // банда для бейджа рядом с ником (может быть null)
                slot: battle.slot,
                you: 1 // Это мы присоединились
              }
            ]
          }
        }
        return b
      })
      
      set({ 
        battles: updatedBattles,
        isLoading: false 
      })
      
      // Воспроизводим звук присоединения к баттлу
      audioService.play('userJoinBattle')
      
      // Если это текущий баттл, обновляем и его
      const { currentBattle } = get()
      if (currentBattle && currentBattle.id === battleId) {
        set({
          currentBattle: {
            ...currentBattle,
            status: battle.status,
            battle_close_in_seconds: battle.battle_close_in_seconds,
            result_casebattle: results,
            players: [
              ...currentBattle.players,
              {
                user_id: battle.user_id,
                user_name: battle.user_name,
                user_ava: battle.user_ava,
                user_team: battle.user_team, // банда для бейджа рядом с ником (может быть null)
                slot: battle.slot,
                you: 1
              }
            ]
          }
        })
      }
      
      // Возвращаем информацию о присоединении
      return {
        battleId: battle.id,
        hasResults: !!results, // true если присоединился последний игрок
        results: results, // Массив результатов розыгрыша или null
        status: battle.status // waiting или finished
      }
    } catch (error) {
      console.error('Join battle error:', error)
      set({
        error: error.message || 'Ошибка присоединения к баттлу',
        isLoading: false
      })
      if (error.code === 'engine_switched' || error.message === 'engine_switched') {
        set({ engine: actionEngine === 'node' ? 'worker' : 'node', hasSnapshot: false, battles: [], battlesById: {} })
        if (actionEngine !== 'node') gameSocket.requestState('casebattle')
      }
      throw error
    }
  },

  /**
   * Обновить данные баттла (вызывается из WebSocket)
   */
  updateBattle: (battleData) => {
    const { currentBattle } = get()
    
    if (currentBattle && currentBattle.id === battleData.id) {
      set({ currentBattle: { ...currentBattle, ...battleData } })
    }

    const { battles } = get()
    const updatedBattles = battles.map(battle => 
      battle.id === battleData.id ? { ...battle, ...battleData } : battle
    )
    set({ battles: updatedBattles })
  },

  /**
   * Добавить новый баттл в список (вызывается из WebSocket)
   */
  addBattle: (battleData) => {
    const { battles } = get()
    const exists = battles.some(b => b.id === battleData.id)
    
    if (!exists) {
      set({ battles: [battleData, ...battles] })
    }
  },

  /**
   * Удалить баттл из списка (вызывается из WebSocket)
   */
  removeBattle: (battleId) => {
    const { battles } = get()
    set({ battles: battles.filter(b => b.id !== battleId) })
  },

  /**
   * Очистить текущий баттл
   */
  clearCurrentBattle: () => {
    set({ currentBattle: null })
  },

  /**
   * Очистить ошибку
   */
  clearError: () => {
    set({ error: null })
  }
}))

export default useBattleStore
